/**
 * One invoice as a list row: number, status chip, who it is for, its dates and its total - and, on
 * the Invoices page, the one action its status allows. Shared by the Invoices page and the customer
 * page, so the two lists cannot come to describe the same invoice differently.
 *
 * The whole card opens the invoice; the Issue and Mark paid buttons inside it still take their own
 * clicks, because `Card.onActivate` hands a click to anything with a tab stop.
 */
import { insets } from 'webwin';
import { InvoiceStatus, type InvoiceDto } from '../../contracts.gen.js';
import { join, localDate, money, plainDate } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { chip } from '../../ui/Chip.js';
import { button, card, fillWith, FONT, GAP, hstack, inlineRow, muted, splitRow, stackOf, value, vstack } from '../../ui/layout.js';

export interface InvoiceCardOptions {
  /** Shown on a list that spans customers; omitted where the page is already about one. */
  showCustomer?: boolean;
  onOpen: (invoice: InvoiceDto) => void;
  /** Offered on a draft. Omit to show no action buttons at all. */
  onIssue?: (invoice: InvoiceDto) => void;
  /** Offered on an issued invoice. */
  onMarkPaid?: (invoice: InvoiceDto) => void;
}

/** Overdue reads as danger, paid as done; every other status is neutral. */
export function invoiceChip(i: InvoiceDto) {
  return chip(i.statusLabel, i.isOverdue ? 'danger' : i.status === InvoiceStatus.Paid ? 'ok' : 'neutral');
}

export function invoiceCard(i: InvoiceDto, options: InvoiceCardOptions): Card {
  const heading = inlineRow();
  heading.controls.add(value(i.number, { font: FONT.bold }));
  heading.controls.add(invoiceChip(i));
  if (i.isRecurring) heading.controls.add(chip('recurring', 'info'));

  const left = stackOf(vstack({ spacing: GAP.xs }), heading);
  if (options.showCustomer) {
    left.controls.add(
      muted(i.customerDeleted ? `${i.customerName} (customer deleted)` : i.customerName, 'subtle')
    );
  }
  // Created is an instant and is shown in local time; due and paid are CALENDAR dates and are
  // printed as stored, because converting them shifts them a day for every reader west of UTC.
  left.controls.add(
    muted(
      join([
        `Created ${localDate(i.createdUtc)}`,
        i.dueUtc ? `due ${plainDate(i.dueUtc)}` : null,
        i.paidUtc ? `paid ${plainDate(i.paidUtc)}` : null
      ])
    )
  );

  const right = stackOf(vstack({ spacing: GAP.sm, autoWidth: true, align: 'end' }));
  right.controls.add(value(money(i.total), { font: FONT.bold }));
  // At most one button, so a plain stack: a WrapPanel needs a width to break against and this
  // column has none - it measures to its content.
  const actions = hstack({ spacing: GAP.sm });
  if (i.isDraft && options.onIssue) {
    const issue = options.onIssue;
    actions.controls.add(button('Issue', () => issue(i), true));
  }
  if (i.isIssued && options.onMarkPaid) {
    const paid = options.onMarkPaid;
    actions.controls.add(button('Mark paid', () => paid(i), true));
  }
  if (actions.controls.length > 0) right.controls.add(actions);

  const row = splitRow({ align: 'center' });
  fillWith(row, left);
  row.controls.add(right);

  const shell = card({ padding: insets(12) });
  shell.controls.add(row);
  shell.onActivate(() => options.onOpen(i));
  return shell;
}
